import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { BaseSafeFormView, BaseText, BaseView } from '@/components/base'
import {
  StickyHeaderNavigation,
  FixedBottomButton,
  FixedBottomTwoButtons,
} from '@/components'
import { Label, LabelAmount } from '@/components/Label'
import { CurrencyItem } from '@/components/CurrencyItem'

import { WalletCurrency } from '@/domain'
import { currencyUtils } from '@/utils'
import { getDisplayCurrencyCode } from '@/utils/getDisplayCurrencyCode'
import { getReferenceColorsForTheCurrency } from '@/utils/getReferenceColorsForTheCurrency'
import { CryptoActions } from './components/CryptoActions'
import { AnimatedLineChart } from './components/AnimatedLineChart'
import { CryptoDescription } from './components/CryptoDescription'
import { LatestActivities } from './components/LatestActivities'
import useOffsets from '@/hooks/useOffsets'

const { currencyConditionals } = currencyUtils

export const CurrencyDetailsScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const scrollViewRef = useRef<HTMLDivElement>(null)
  const offsets = useOffsets()

  // Get currency data from navigation state
  const currency = location.state as WalletCurrency

  // Scroll to top on page open
  React.useEffect(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ top: 0, behavior: 'instant' })
    }
    // Also scroll the entire window to top just in case
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  // Redirect to dashboard if no currency data
  React.useEffect(() => {
    if (!currency || !currency.code) {
      navigate('/dashboard')
    }
  }, [currency, navigate])

  if (!currency || !currency.code) {
    return null
  }

  const { isStablecoin } = currencyConditionals(currency.code || '')

  const handlePressAddMoney = () => {
    navigate('/add-money/select-deposit-type', {
      state: {
        ...currency,
        isBuy: isStablecoin ? '0' : '1',
      },
    })
  }

  const handlePressSell = () => {
    navigate('/sell-money/select-currency-sell', {
      state: { ...currency },
    })
  }

  const handleBackPress = () => {
    navigate('/dashboard')
  }

  return (
    <BaseSafeFormView
      style={{
        paddingTop: `${offsets.top + 70}px`,
        height: '100vh',
        // paddingLeft: '13px',
        // paddingRight: '13px',
      }}
      flex={1}
      bgColor="background.screen"
    >
      <StickyHeaderNavigation
        title={getDisplayCurrencyCode(currency?.code || '')}
        onBackPress={handleBackPress}
      />
      <BaseView flex={1} px={13}>
        {/* Fixed Header Navigation */}

        {/* Scrollable Content */}
        <div
          ref={scrollViewRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            paddingBottom: '120px', // Padding for fixed buttons
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          {/* Main content */}
          <BaseView gap={isStablecoin ? 60 : 24}>
            {/* Animated Line Chart for non-stablecoins */}
            {!isStablecoin && (
              <AnimatedLineChart
                code={currency.code}
                cryptoName={currency.name}
                referenceColor={
                  getReferenceColorsForTheCurrency({
                    currencyCode: currency.code,
                  }).lineChartRefColor
                }
                scrollViewRef={scrollViewRef}
                pricePerUnit={currency.pricePerUnit}
                percentageChange24h={currency.percentageChange24h}
              />
            )}

            {/* Balance display for stablecoins */}
            {isStablecoin && (
              <BaseView center gap={24} px={12}>
                <Label type="descriptionBottomSheet">
                  {t('translation:totalBalance')}
                </Label>
                <LabelAmount
                  amount={String(
                    currency.amount || currency.amountWithoutNumberConversion,
                  )}
                  currencyCode={currency.code}
                  showDecimals
                  shouldMaskStablecoinAsFiat
                />
              </BaseView>
            )}

            {/* Crypto Actions - change later */}
            <CryptoActions walletSelected={currency} />
          </BaseView>
          {/* End of Main content */}

          {/* Additional Content */}
          <BaseView>
            <BaseView
              data-component="currency-details-additional-content"
              gap={4}
            >
              {/* Total Balance for non-stablecoins */}
              {!isStablecoin && (
                <BaseView gap={6}>
                  <BaseText
                    fontSize={18}
                    fontFamily="semibold"
                    color="label.default"
                  >
                    {t('home.totalBalance')}
                  </BaseText>
                  <CurrencyItem
                    descriptionIsCryptoValue
                    isRelativeFiatValueInUSD
                    containerStyle={{
                      backgroundColor: 'transparent',
                      paddingLeft: 0,
                      paddingRight: 0,
                      paddingBottom: 0,
                    }}
                    contentStyle={{
                      gap: '8px',
                    }}
                    {...currency}
                  />
                </BaseView>
              )}

              {/* Crypto Description */}
              <CryptoDescription
                cryptoCode={currency.code}
                cryptoName={currency.name}
              />

              {/* Latest Activities */}
              <LatestActivities {...currency} />
            </BaseView>
          </BaseView>
        </div>
      </BaseView>

      {/* Fixed Bottom Buttons */}
      {isStablecoin ? (
        <FixedBottomButton
          buttons={{
            label: t('form:actions.addMoney'),
            iconName: 'plusCircle',
            iconSize: 22,
            iconGap: 6,
            type: 'secondaryGray',
            onPress: handlePressAddMoney,
          }}
        />
      ) : (
        <FixedBottomTwoButtons
          leftButton={{
            label: t('form:actions.buy'),
            type: 'secondaryGray',
            onPress: handlePressAddMoney,
          }}
          rightButton={{
            label: t('form:actions.sell'),
            type: 'secondaryGray',
            onPress: handlePressSell,
          }}
        />
      )}
    </BaseSafeFormView>
  )
}
